import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAuthSettings } from '../context/AuthSettingsContext';
import { authApi } from '../api/auth';
import { Button, Input, Card, CardContent } from '../components/ui';
import loginBackground from '../assets/login_background.jpg';
import logoEquancy from '../assets/logo_equancy.png';
import { AlertCircle, KeyRound } from 'lucide-react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  const { login, isAuthenticated, loading } = useAuth();
  const { isLocalPasswordEnabled, loading: settingsLoading } = useAuthSettings();

  useEffect(() => {
    setError('');
  }, [email, password]);


  const handleMicrosoftLogin = () => {
    authApi.microsoftLogin();
  };

  if (loading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/staffing" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundImage: `url(${loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >      
      <div className="max-w-md w-full space-y-8 relative z-10 animate-scale-in">
        <Card className="shadow-soft-xl border-0 backdrop-blur-sm bg-white/95">
          <CardContent className="p-10 relative">
            <div className="text-center mb-10">
              <div className="mx-auto mb-6">
                <img 
                  src={logoEquancy} 
                  alt="Logo Equancy" 
                  className="w-auto mx-auto"
                />
              </div>
              <h1 className="heading-lg text-neutral-900">
                Staffing Equancy
              </h1>
              <p className="mt-3 text-body text-neutral-600">
                Connectez-vous à votre compte
              </p>
            </div>

            {error && (
              <div className="status-error rounded-xl p-4 mb-6 flex items-start space-x-3 animate-slide-down">
                <AlertCircle className="h-5 w-5 text-error-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-error-700 font-medium">{error}</div>
              </div>
            )}

            {/* Microsoft first: it is how everyone signs in. */}
            <Button
              type="button"
              onClick={handleMicrosoftLogin}
              size="lg"
              className="w-full flex items-center justify-center space-x-3"
              disabled={isLoading}
            >
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M11 11V0H0V11H11Z" fill="#F25022"/>
                <path d="M23 11V0H12V11H23Z" fill="#7FBA00"/>
                <path d="M11 23V12H0V23H11Z" fill="#00A4EF"/>
                <path d="M23 23V12H12V23H23Z" fill="#FFB900"/>
              </svg>
              <span className="font-semibold">Continuer avec Microsoft</span>
            </Button>
            <p className="mt-3 text-center text-xs text-neutral-500">Votre compte Equancy, sans mot de passe à retenir.</p>

            {/* Password: a second option, kept for tests outside the SSO (test accounts, specific profiles). */}
            {isLocalPasswordEnabled && (
              <div className="mt-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-neutral-500 font-medium">ou</span>
                  </div>
                </div>

                {!showPasswordForm ? (
                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={() => setShowPasswordForm(true)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-primary-700"
                    >
                      <KeyRound className="h-4 w-4" aria-hidden="true" />
                      Se connecter avec un mot de passe
                    </button>
                    <p className="mt-2 text-xs text-neutral-500">
                      Réservé aux tests hors SSO : comptes de test et profils particuliers.
                    </p>
                  </div>
                ) : (
                  <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
                    <p className="text-xs text-neutral-500">
                      Connexion par mot de passe, réservée aux tests hors SSO : comptes de test et profils particuliers.
                    </p>
                    <Input
                      label="Adresse email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      placeholder="votre@email.com"
                      size="lg"
                      autoFocus
                    />

                    <Input
                      label="Mot de passe"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="••••••••"
                      size="lg"
                    />

                    <Button
                      type="submit"
                      variant="secondary"
                      className="w-full"
                      size="lg"
                      loading={isLoading}
                      disabled={!email || !password}
                    >
                      Se connecter
                    </Button>
                  </form>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-sm text-white/90 font-medium backdrop-blur-sm bg-black/20 rounded-full px-4 py-2 inline-block">
            Plateforme de gestion des consultants Equancy
          </p>
        </div>
      </div>
    </div>
  );
}